import { useEffect, useState } from 'react';
import { Button, Field, Input, Select, Textarea } from '../ui';
import { Dialog } from './Dialog';
import { BroadcastsAPI } from '../../services/endpoints/admin';
import type { BroadcastSendPayload } from '../../services/endpoints/admin';
import { useDispatchAction } from '../../hooks/useDispatchAction';
import type { BroadcastAudience, BroadcastCountry } from '../../types/api';

type UserMode = 'EMAIL' | 'DEVICE_ID';

/** A single recipient to lock the dialog to (push goes only to this person). When
 *  set, the audience picker is hidden and the broadcast is sent as audience=USER
 *  targeting the given email. Used by the Users list / user detail "Message" action. */
export interface LockedRecipient {
  email: string;
  name?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSent: () => void;
  /** Lock the send to one user (hides audience controls). Omit for the full
   *  audience-picker compose flow used on the Broadcasts page. */
  lockedRecipient?: LockedRecipient;
}

/**
 * Compose + send an FCM push. Two modes:
 *  - Full (no `lockedRecipient`): pick an audience (all / subscribers / a specific
 *    user / country) — the original Broadcasts-page composer.
 *  - Locked (`lockedRecipient` set): a focused "message this user" form — just a
 *    title + message, pinned to that user's email.
 * Both share one two-step review→send flow and call `BroadcastsAPI.send` directly.
 */
export function ComposeBroadcastDialog({ open, onClose, onSent, lockedRecipient }: Props) {
  const locked = !!lockedRecipient;
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [audience, setAudience] = useState<BroadcastAudience>('SUBSCRIBERS');
  const [userMode, setUserMode] = useState<UserMode>('EMAIL');
  const [userValue, setUserValue] = useState('');
  const [country, setCountry] = useState<BroadcastCountry>('NGN');
  const [confirming, setConfirming] = useState(false);
  const { run, busy } = useDispatchAction();

  // Reset transient state whenever the dialog (re)opens so a previous draft or a
  // different locked recipient never bleeds across opens.
  useEffect(() => {
    if (!open) return;
    setTitle('');
    setContent('');
    setConfirming(false);
    if (locked) {
      setAudience('USER');
      setUserMode('EMAIL');
      setUserValue(lockedRecipient!.email);
    } else {
      setAudience('SUBSCRIBERS');
      setUserMode('EMAIL');
      setUserValue('');
      setCountry('NGN');
    }
  }, [open, locked, lockedRecipient]);

  async function send() {
    const payload: BroadcastSendPayload = {
      title: title.trim(),
      content: content.trim(),
      audience,
    };
    if (audience === 'USER') {
      if (userMode === 'EMAIL') payload.userEmail = userValue.trim();
      else                       payload.userDeviceId = userValue.trim();
    }
    if (audience === 'COUNTRY') payload.country = country;

    const ok = await run('Send message', () => BroadcastsAPI.send(payload));
    if (ok !== null) {
      setTitle(''); setContent('');
      if (!locked) setUserValue('');
      onSent();
      setConfirming(false);
    }
  }

  const needsUser    = audience === 'USER';
  const needsCountry = audience === 'COUNTRY';
  const canReview =
    !!title.trim() && !!content.trim() &&
    (!needsUser    || !!userValue.trim()) &&
    (!needsCountry || !!country);

  const recipientLabel = lockedRecipient?.name
    ? `${lockedRecipient.name} · ${lockedRecipient.email}`
    : lockedRecipient?.email ?? '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={locked ? 'Message user' : 'Compose broadcast'}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button tone="ember" onClick={() => setConfirming(true)} disabled={busy || !canReview}>
            Review &amp; send
          </Button>
        </>
      }>
      {locked && (
        <Field label="To">
          <div className="nb-recipient">
            <span className="nb-recipient__icon" aria-hidden>✉</span>
            <span className="nb-recipient__name">{recipientLabel}</span>
            <span className="nb-recipient__hint">Direct push · this user only</span>
          </div>
        </Field>
      )}

      <Field label="Title"><Input value={title} onChange={e => setTitle(e.target.value)} placeholder="A short headline" autoFocus /></Field>
      <Field label="Message">
        <Textarea value={content} onChange={e => setContent(e.target.value)} rows={6} placeholder="Plain language. State the why, the when, and any action." />
      </Field>

      {!locked && (
        <>
          <div className="nb-form-row nb-form-row--two">
            <Field label="Audience">
              <Select value={audience} onChange={e => setAudience(e.target.value as BroadcastAudience)}>
                <option value="ALL">All users</option>
                <option value="SUBSCRIBERS">Subscribers (active or expired)</option>
                <option value="USER">Specific user</option>
                <option value="COUNTRY">By country</option>
              </Select>
            </Field>
            {needsUser && (
              <Field label={userMode === 'EMAIL' ? 'User email' : 'Device ID'}>
                <Input
                  value={userValue}
                  onChange={e => setUserValue(e.target.value)}
                  placeholder={userMode === 'EMAIL' ? 'user@example.com' : 'device UUID'}
                />
              </Field>
            )}
            {needsCountry && (
              <Field label="Country">
                <Select value={country} onChange={e => setCountry(e.target.value as BroadcastCountry)}>
                  <option value="NGN">Nigeria (NGN)</option>
                  <option value="OTHER">All others</option>
                </Select>
              </Field>
            )}
          </div>
          {needsUser && (
            <div className="nb-form-row" style={{ marginTop: 4 }}>
              <Field label="Identify by">
                <div style={{ display: 'flex', gap: 16 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="radio" name="userMode" checked={userMode === 'EMAIL'} onChange={() => setUserMode('EMAIL')} />
                    Email
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="radio" name="userMode" checked={userMode === 'DEVICE_ID'} onChange={() => setUserMode('DEVICE_ID')} />
                    Device ID
                  </label>
                </div>
              </Field>
            </div>
          )}
        </>
      )}

      <Dialog open={confirming} onClose={() => setConfirming(false)} title="Confirm message"
        footer={<><Button onClick={() => setConfirming(false)}>Back</Button><Button tone="ember" onClick={send} disabled={busy}>{busy ? 'Sending…' : 'Send now'}</Button></>}>
        <p className="nb-prose-italic">
          About to push <strong>{title || '(no title)'}</strong> to <strong>{audienceSummary(audience, userMode, userValue, country, lockedRecipient)}</strong>.
          Dispatch runs in the background; the row on the Broadcasts page updates with delivery counts as Firebase responds.
        </p>
      </Dialog>
    </Dialog>
  );
}

function audienceSummary(a: BroadcastAudience, mode: UserMode, val: string, country: BroadcastCountry, locked?: LockedRecipient): string {
  if (locked) return locked.name ? `${locked.name} (${locked.email})` : locked.email;
  switch (a) {
    case 'ALL':          return 'all users';
    case 'SUBSCRIBERS':  return 'subscribers (active or expired)';
    case 'USER':         return mode === 'EMAIL' ? `user with email ${val}` : `device id ${val}`;
    case 'COUNTRY':      return country === 'NGN' ? 'users in Nigeria' : 'users outside Nigeria';
    default:             return String(a);
  }
}
